import React, { useEffect, useState } from 'react';
import { OnboardingTask, OnboardingTaskKey } from 'types/interfaces/onboarding';
import { Points } from 'components/Points/Points';
import MagicButton from 'components/MagicButton/MagicButton';
import './ChecklistTask.css';

interface ChecklistTaskProps {
  task: OnboardingTask;
  onStartTask: (task: OnboardingTask) => void;
  onClaimPoints: (taskKey: OnboardingTaskKey) => void;
  onConfirmSocialFollow: (taskKey: OnboardingTaskKey) => void;
  onCancelSocialFollow: () => void;
  isConfirmingFollow: boolean;
  isAnimating: boolean;
  onAnimationComplete: (taskKey: OnboardingTaskKey) => void;
  isConfirming: boolean;
  isClaiming: boolean;
}

const ChecklistTask: React.FC<ChecklistTaskProps> = ({
  task,
  onStartTask,
  onClaimPoints,
  onConfirmSocialFollow,
  onCancelSocialFollow,
  isConfirmingFollow,
  isAnimating,
  onAnimationComplete,
  isConfirming,
  isClaiming,
}) => {
  const [showPointsBadge, setShowPointsBadge] = useState(false);

  // Handle animation sequence
  useEffect(() => {
    if (isAnimating) {
      // Show points badge after glow
      const pointsTimer = setTimeout(() => {
        setShowPointsBadge(true);
      }, 300);

      // Complete animation after full sequence
      const completeTimer = setTimeout(() => {
        onAnimationComplete(task.taskKey);
      }, 1200);

      return () => {
        clearTimeout(pointsTimer);
        clearTimeout(completeTimer);
      };
    }
  }, [isAnimating, onAnimationComplete, task.taskKey]);

  const isLocked = task.status === 'LOCKED';
  const isCompleted = task.status === 'COMPLETED';
  const isAvailable = task.status === 'AVAILABLE';

  // New state: task is completed but points haven't been claimed yet
  const isClaimable = isCompleted && !task.pointsAwarded;
  // Task is fully done (completed AND points claimed)
  const isFullyComplete = isCompleted && task.pointsAwarded;

  const handleButtonClick = () => {
    if (isClaimable) {
      onClaimPoints(task.taskKey);
    } else if (isConfirmingFollow) {
      onConfirmSocialFollow(task.taskKey);
    } else {
      onStartTask(task);
    }
  };

  const handleRowClick = () => {
    // Trigger for claimable tasks
    if (isClaimable && !isClaiming) {
      onClaimPoints(task.taskKey);
      return;
    }
    // Trigger for available tasks that aren't in confirming state
    if (isAvailable && !isConfirmingFollow && !isConfirming) {
      onStartTask(task);
    }
  };

  const handleCancelClick = () => {
    onCancelSocialFollow();
  };

  const getButtonText = () => {
    if (isClaiming) return 'Claiming...';
    if (isClaimable) return 'Claim';
    if (isConfirming) return 'Confirming...';
    if (isConfirmingFollow) return 'I followed';
    if (isLocked) return 'Locked';
    return 'Start';
  };

  const getButtonColor = (): 'black' | 'purple' | 'grey' => {
    if (isClaimable) return 'purple';
    if (isConfirmingFollow) return 'purple';
    return 'black'; // Black button for Start
  };

  const getStatusIcon = () => {
    if (isCompleted || isAnimating) {
      return (
        <div className={`task-icon completed ${isAnimating ? 'animating' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M13.3 4.3L6 11.6L2.7 8.3"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      );
    }
    if (isLocked) {
      return (
        <div className="task-icon locked">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="3" y="7" width="10" height="7" rx="1" stroke="currentColor" strokeWidth="1.5" />
            <path
              d="M5 7V5C5 3.34 6.34 2 8 2C9.66 2 11 3.34 11 5V7"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      );
    }
    return <div className="task-icon available" />;
  };

  // Build class names for the task row
  const taskClasses = [
    'checklist-task',
    task.status.toLowerCase(),
    isAnimating ? 'animating' : '',
    isClaimable ? 'claimable' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={taskClasses}
      role="listitem"
      onClick={handleRowClick}
    >
      <div className="task-status">
        {getStatusIcon()}
      </div>

      <div className="task-content">
        <div className="task-title">{task.title}</div>
        <div className="task-description">{task.description}</div>
        {task.progress && isAvailable && (
          <div className="task-progress">
            <div className="task-progress-bar">
              <div
                className="task-progress-fill"
                style={{ width: `${(task.progress.current / task.progress.target) * 100}%` }}
              />
            </div>
            <span className="task-progress-text">
              {task.progress.current}/{task.progress.target}
            </span>
          </div>
        )}
      </div>

      <div className="task-points">
        <Points type="small" points={task.pointsReward} />
      </div>

      <div className="task-action" onClick={(e) => e.stopPropagation()}>
        {isConfirmingFollow && (
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={handleCancelClick}
            isDisabled={isConfirming}
          />
        )}
        <MagicButton
          label={getButtonText()}
          color={getButtonColor()}
          onPress={handleButtonClick}
          isDisabled={isLocked || isFullyComplete}
          isLoading={isConfirming || isClaiming}
        />
      </div>

      {/* Animated points badge */}
      {showPointsBadge && isAnimating && (
        <div className="floating-points">
          <span className="floating-plus">+</span>
          <Points type="small" points={task.pointsReward} />
        </div>
      )}
    </div>
  );
};

export default ChecklistTask;
